import React from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { DSButton, DSText } from '@zoe/ds-web'
import { onNextClickType } from 'components/types'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { TypewriterText } from '../core/text/TypewriterText'
import { SCREEN_MAX_WIDTH } from '../core/constants'
import { Quizzes } from '../../redux/quiz/types'
import { GIFS, GifName, gifSrc } from '../../data/gifs'

// A full-bleed Ziggie with a speech bubble above it. Continue walks through
// `messages` one bubble at a time while Ziggie keeps looping, then moves on to
// the next screen after the last one.

export type ZiggieTalkScreenProps = {
  gif: GifName
  // One speech bubble per message. Supports <b> and quiz tokens.
  messages: string[]
  buttonText?: string
}

type Props = ZiggieTalkScreenProps & {
  onNextClick: onNextClickType
  quiz: Quizzes
}

const bubbleIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.92); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`

/* Grows out of the square bottom-left corner, which reads as the bubble's tail. */
const Bubble = styled.div`
  position: relative;
  z-index: 1;
  background: var(--colour-surface-white);
  border-radius: 20px 20px 20px 0;
  padding: var(--grid-16) 20px;
  transform-origin: bottom left;
  animation: ${bubbleIn} 0.35s cubic-bezier(0.2, 0.8, 0.3, 1.2) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

/* Pinned to the viewport rather than the scroll region so Ziggie sits behind
   both the content and the CTA bar, as in the design. Geometry from Figma at
   375px wide: Ziggie's frame is 566px wide, centred, and its bottom edge runs
   188px below the visible area, so only the head and shoulders show above the
   CTA. */
/* Capped at the GIF's own width: drawn any bigger, the browser stretches it and
   Ziggie looks blurry on large screens. The bottom offset follows the drawn
   width (0.502 / 1.51 of it) so the crop stays the same. */
const ZiggieLayer = styled.img<{ $maxWidth: number }>`
  --screen-width: min(100vw, ${SCREEN_MAX_WIDTH}px);
  --ziggie-width: min(var(--screen-width) * 1.51, ${({ $maxWidth }) => $maxWidth}px);
  position: fixed;
  left: 50%;
  bottom: calc(var(--ziggie-width) * -0.3325);
  width: var(--ziggie-width);
  transform: translateX(-50%);
  pointer-events: none;
`

const TransparentFooter = styled(StickyFooter)`
  && {
    background: transparent;
  }
  &&::before {
    display: none;
  }
`

export const ZiggieTalkScreen: React.FC<Props> = ({ gif, messages, buttonText, onNextClick, quiz }) => {
  const history = useHistory<{ step?: number } | undefined>()
  const location = useLocation<{ step?: number } | undefined>()
  // Each step is its own history entry, so back (header or browser) returns to
  // the previous message before leaving the screen.
  const step = Math.min(location.state?.step ?? 0, messages.length - 1)
  const [typedUpTo, setTypedUpTo] = React.useState(-1)
  const typed = step <= typedUpTo
  const isLastStep = step >= messages.length - 1

  // A tap while Ziggie is still typing finishes the message rather than skipping it.
  const onContinue = () => {
    if (!typed) return setTypedUpTo((upTo) => Math.max(upTo, step))
    if (isLastStep) return onNextClick()
    history.push({ pathname: location.pathname, search: location.search, state: { step: step + 1 } })
  }

  return (
    <>
      <ZiggieLayer src={gifSrc(gif)} $maxWidth={GIFS[gif].width} alt="" aria-hidden />
      <ScreenContainer>
        <div aria-live="polite">
          <Bubble key={step} data-testid="ziggie-speech-bubble">
            <DSText variant="fluid-paragraph-400">
              <TypewriterText
                text={messages[step]}
                quiz={quiz}
                complete={typed}
                onComplete={() => setTypedUpTo((upTo) => Math.max(upTo, step))}
              />
            </DSText>
          </Bubble>
        </div>
      </ScreenContainer>
      <TransparentFooter>
        <StickyFooterContent>
          {/* Surface navy, not the usual yellow, so it sits apart from Ziggie. */}
          <DSButton
            variant="primary"
            colorScheme="inverse"
            size="large"
            width="full"
            onPress={onContinue}
            testId="continue-button"
          >
            <DSText variant="fluid-paragraph-200" weight="semiBold" color="primary-inverse">
              {buttonText ?? 'Continue'}
            </DSText>
          </DSButton>
        </StickyFooterContent>
      </TransparentFooter>
    </>
  )
}
